if (!customElements.get('product-modal')) {
  customElements.define('product-modal', class ProductModal extends ModalDialog {
    constructor() {
      super();
      this.prevButton = this.querySelector('[data-media-modal-prev]');
      this.nextButton = this.querySelector('[data-media-modal-next]');
      this.counter = this.querySelector('[data-media-modal-counter]');

      if (this.prevButton) this.prevButton.addEventListener('click', this.showPrevious.bind(this));
      if (this.nextButton) this.nextButton.addEventListener('click', this.showNext.bind(this));

      // ModalDialog's own constructor (see global.js) closes the whole modal
      // on ANY mouse pointerup that isn't inside <deferred-media>/<product-model>
      // — the prev/next buttons don't match that, so clicking them was
      // closing the lightbox instead of stepping through images. Stop the
      // pointerup here so it never reaches that "click outside = close"
      // listener on the modal element.
      if (this.prevButton) this.prevButton.addEventListener('pointerup', (e) => e.stopPropagation());
      if (this.nextButton) this.nextButton.addEventListener('pointerup', (e) => e.stopPropagation());

      this.addEventListener('keydown', this.onKeydown.bind(this));

      /* Touch swipe — left = next, right = previous. */
      this.touchStartX = null;
      this.touchStartY = null;
      const content = this.querySelector('.product-media-modal__content');
      if (content) {
        content.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: true });
        content.addEventListener('touchend', this.onTouchEnd.bind(this), { passive: true });
      }
    }

    hide() {
      super.hide();
      // Matches the cart drawer / size guide / inscription drawer pattern:
      // ModalDialog's own show()/hide() only toggle body.overflow-hidden,
      // which doesn't stop Lenis's own smooth-scroll loop from still
      // driving the page underneath a fixed-position modal.
      if (window.__lenis) window.__lenis.start();
      // ModalDialog.hide() (see global.js) calls window.pauseAllMedia(),
      // which pauses EVERY <video> on the page — not just this modal's own,
      // but also the gallery thumbnail's autoplaying video sitting behind
      // it (see product-thumbnail.liquid / product-redesign.css). Nothing
      // else ever resumes those, so closing the lightbox silently killed
      // the gallery's autoplay for good. Restart them here.
      document.querySelectorAll('.product-video-autoplay video').forEach((video) => {
        const playPromise = video.play();
        if (playPromise && playPromise.catch) playPromise.catch(() => {});
      });
    }

    show(opener) {
      super.show(opener);
      if (window.__lenis) window.__lenis.stop();
      this.showActiveMedia();
    }

    // Plays the <video> inside a given slide, if it has one — ModalDialog's
    // own show() (see global.js) calls window.pauseAllMedia() BEFORE this
    // runs, which pauses every video on the page including whichever one is
    // about to become the active slide. The `autoplay` attribute on that
    // <video> only fires once, on initial insertion into the DOM — it does
    // NOT retry just because the element becomes visible again after being
    // paused, so without this the enlarged video sat frozen on its poster
    // frame instead of actually playing enlarged.
    playActiveVideo(mediaEl) {
      const video = mediaEl && mediaEl.querySelector && mediaEl.querySelector('video');
      if (!video) return;
      const playPromise = video.play();
      if (playPromise && playPromise.catch) playPromise.catch(() => {});
    }

    getSlides() {
      return Array.from(this.querySelectorAll('.product-media-modal__content > [data-media-id]'));
    }

    getActiveIndex(slides) {
      const list = slides || this.getSlides();
      return list.findIndex((element) => element.classList.contains('active'));
    }

    showActiveMedia() {
      this.querySelectorAll(`[data-media-id]:not([data-media-id="${this.openedBy.getAttribute("data-media-id")}"])`).forEach((element) => {
          element.classList.remove('active');
        }
      )
      const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute("data-media-id")}"]`);
      const activeMediaTemplate = activeMedia.querySelector('template');
      const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
      activeMedia.classList.add('active');
      activeMedia.scrollIntoView();

      const container = this.querySelector('[role="document"]');
      container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

      if (activeMedia.nodeName == 'DEFERRED-MEDIA' && activeMediaContent && activeMediaContent.querySelector('.js-youtube'))
        activeMedia.loadContent();

      this.playActiveVideo(activeMedia);
      this.updateNavState();
    }

    goToIndex(index) {
      const slides = this.getSlides();
      if (!slides.length) return;
      const nextIndex = ((index % slides.length) + slides.length) % slides.length; // wrap both directions
      slides.forEach((slide) => slide.classList.remove('active'));
      const nextSlide = slides[nextIndex];
      nextSlide.classList.add('active');

      const deferred = nextSlide.matches('deferred-media, product-model') ? nextSlide : nextSlide.querySelector('deferred-media, product-model');
      if (deferred && typeof deferred.loadContent === 'function') {
        const template = deferred.querySelector('template');
        const content = template ? template.content : null;
        if (content && content.querySelector('.js-youtube')) deferred.loadContent();
      } else {
        // Stops whatever was playing on the slide being left (a native
        // <video> has no deferred-media wrapper to intercept, so it falls
        // in here too) — playActiveVideo() below then explicitly starts the
        // new slide's own video, if it has one.
        window.pauseAllMedia();
      }

      this.playActiveVideo(nextSlide);
      this.updateNavState();
    }

    showNext() {
      const slides = this.getSlides();
      this.goToIndex(this.getActiveIndex(slides) + 1);
    }

    showPrevious() {
      const slides = this.getSlides();
      this.goToIndex(this.getActiveIndex(slides) - 1);
    }

    updateNavState() {
      const slides = this.getSlides();
      const hasMultiple = slides.length > 1;
      if (this.prevButton) this.prevButton.classList.toggle('product-media-modal__nav--is-hidden', !hasMultiple);
      if (this.nextButton) this.nextButton.classList.toggle('product-media-modal__nav--is-hidden', !hasMultiple);
      if (this.counter) {
        this.counter.textContent = hasMultiple
          ? `${this.getActiveIndex(slides) + 1} / ${slides.length}`
          : '';
      }
    }

    onKeydown(event) {
      if (!this.hasAttribute('open')) return;
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.showNext();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.showPrevious();
      }
    }

    onTouchStart(event) {
      const touch = event.changedTouches[0];
      this.touchStartX = touch.clientX;
      this.touchStartY = touch.clientY;
    }

    onTouchEnd(event) {
      if (this.touchStartX === null) return;
      const touch = event.changedTouches[0];
      const deltaX = touch.clientX - this.touchStartX;
      const deltaY = touch.clientY - this.touchStartY;
      this.touchStartX = null;
      this.touchStartY = null;

      const SWIPE_THRESHOLD = 40;
      /* Ignore mostly-vertical drags so the modal's own vertical scroll still works. */
      if (Math.abs(deltaX) < SWIPE_THRESHOLD || Math.abs(deltaX) < Math.abs(deltaY)) return;

      if (deltaX < 0) {
        this.showNext();
      } else {
        this.showPrevious();
      }
    }
  });
}
